import React, { createContext, useState, useMemo, useCallback, useEffect, useContext } from 'react';
import { get, isEmpty, isEqual } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { cleanObject } from 'utils/general';
import useSessionStorage from 'hooks/useSessionStorage';
import { Roles } from 'contexts/Roles.context';

export const UserProfile = createContext({});

const cleanInlineFilterObjects = ({ rawSelectedProfile, newFilters }) => {
  let rawFilter = get(rawSelectedProfile, 'value');
  let newFilter = get(newFilters, 'value');

  if (get(newFilter, 'inline')) {
    const cleanInlineNewFilter = cleanObject(get(newFilter, 'inline'));
    newFilter = {
      ...newFilter,
      inline: isEmpty(cleanInlineNewFilter) ? null : cleanInlineNewFilter,
    };
  }

  if (get(rawFilter, 'inline')) {
    const cleanInlineRawFilter = cleanObject(get(rawFilter, 'inline'));
    rawFilter = {
      ...rawFilter,
      inline: isEmpty(cleanInlineRawFilter) ? null : cleanInlineRawFilter,
    };
  }

  return { rawFilter, newFilter };
};

const handleLegacyValues = ({ selectedUserProfile, setSelectedUserProfile }) => {
  let isChanged = false;
  if (isEmpty(selectedUserProfile)) {
    return;
  }
  const selectedUserProfileCopy = { ...selectedUserProfile };
  if (get(selectedUserProfileCopy, 'value.fullPageFiltersRequest')) {
    delete selectedUserProfileCopy.value.fullPageFiltersRequest;
    isChanged = true;
  }

  if (get(selectedUserProfileCopy, 'value.fullPageFiltersStored')) {
    delete selectedUserProfileCopy.value.fullPageFiltersStored;
    isChanged = true;
  }
  if (isChanged) {
    setSelectedUserProfile(selectedUserProfileCopy);
  }
};

const UserProfileProvider = ({ children }) => {
  const location = useLocation();
  const { userInfo, isUserInfoLoading } = useContext(Roles);

  const navigate = useNavigate();

  const {
    clearStorage,
    value: selectedUserProfile,
    setStoredValue: setSelectedUserProfile,
    isValueSet,
  } = useSessionStorage('userProfile');

  const [userProfiles, setUserProfiles] = useState(null);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const [isProfilesLoading, setIsProfilesLoading] = useState(true);
  const [isTemporaryProfile, setIsTemporaryProfile] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);

  // apply filters on ?filter param -> used for chatbot link
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const filters = qs.get('filters');

    if (!filters) return;

    if (filters.includes('customerRequestType') && location.pathname !== '/customer-requests') {
      navigate(`/customer-requests?filters=${filters}`);
    }

    try {
      const filter = JSON.parse(filters);
      clearStorage();
      setSelectedUserProfile({
        value: {
          inline: { filter: filter?.and?.[0], filterSynthetic: {} },
          table: 'bySeriesVin',
        },
        path: location.pathname,
      });
      window.location.search = '';
      setIsFilterChanged(true);
    } catch (error) {
      setSelectedUserProfile(null);
    }
  }, [location.search]);

  const hasSharedRole = selectedUserProfile?.roles?.some((role) =>
    userInfo()?.roles?.includes(role),
  );

  const isAccessUnavailable = !isUserInfoLoading && !hasSharedRole;
  //Does not set profile when access denied
  if (selectedUserProfile?.isPublished && isAccessUnavailable) {
    setAccessModalOpen(true);
    setSelectedUserProfile(null);
    setIsFilterChanged(false);
  }

  //Reset page on lens change
  if (selectedUserProfile?.path && location?.pathname !== selectedUserProfile?.path) {
    setSelectedUserProfile(null);
    setIsFilterChanged(false);
    setIsTemporaryProfile(false);
  }

  useEffect(() => {
    if (isValueSet && !isProfilesLoading) {
      checkIsProfileChanged(selectedUserProfile);
    }
  }, [isProfilesLoading]);

  useEffect(
    () => handleLegacyValues({ selectedUserProfile, setSelectedUserProfile }),
    [selectedUserProfile],
  );

  const setFilerChange = (isChanged) => {
    setIsFilterChanged(isChanged);
  };

  const checkIsProfileChanged = (newFilters) => {
    const rawSelectedProfile = userProfiles?.find(
      (item) => item.key === get(selectedUserProfile, 'key'),
    );

    const { rawFilter, newFilter } = cleanInlineFilterObjects({ rawSelectedProfile, newFilters });

    if (!isEmpty(rawFilter) && !isEmpty(newFilter)) {
      const isSameFilter = isEqual(rawFilter, newFilter);
      setIsFilterChanged(!isSameFilter);
    } else {
      setIsFilterChanged(false);
    }
  };

  const setFilters = ({ filterType, filters }) => {
    if (isProfilesLoading) {
      return;
    }
    const newFilters = {
      ...(selectedUserProfile || {}),
      value: {
        ...get(selectedUserProfile, 'value', {}),
        [filterType]: filters,
      },
      path: location?.pathname,
    };
    checkIsProfileChanged(newFilters);
    setSelectedUserProfile(newFilters);
  };

  const setMultipleFilters = (filters = {}) => {
    if (isProfilesLoading) {
      return;
    }
    const newFilters = {
      ...(selectedUserProfile || {}),
      value: {
        ...get(selectedUserProfile, 'value', {}),
        ...filters,
      },
      path: location?.pathname,
    };
    checkIsProfileChanged(newFilters);
    setSelectedUserProfile(newFilters);
  };

  const setFiltersOverwrite = (filters = {}) => {
    if (isProfilesLoading) {
      return;
    }
    const newFilters = {
      ...(selectedUserProfile || {}),
      value: {
        ...filters,
      },
      path: location?.pathname,
    };
    checkIsProfileChanged(newFilters);
    setSelectedUserProfile(newFilters);
  };

  const dispatchProfiles = useCallback((profiles) => {
    setUserProfiles(profiles);
  }, []);

  const dispatchSelectedProfile = useCallback((selectedProfile) => {
    if (selectedProfile === null) {
      setIsFilterChanged(false);
    } else if (!selectedProfile?.key) {
      setIsFilterChanged(true);
    } else {
      setIsFilterChanged(false);
    }

    setSelectedUserProfile(selectedProfile);
  }, []);

  const clearUserProfileStorage = () => clearStorage();

  const contextValue = useMemo(
    () => ({
      accessModalOpen,
      clearUserProfileStorage,
      dispatchProfiles,
      dispatchSelectedProfile,
      isFilterChanged,
      isProfilesLoading,
      isTemporaryProfile,
      selectedUserProfile,
      setAccessModalOpen,
      setFilerChange,
      setFilters,
      setFiltersOverwrite,
      setIsFilterChanged,
      setIsProfilesLoading,
      setIsTemporaryProfile,
      setMultipleFilters,
      userProfiles,
    }),
    [
      accessModalOpen,
      clearUserProfileStorage,
      dispatchProfiles,
      dispatchSelectedProfile,
      isFilterChanged,
      isProfilesLoading,
      isTemporaryProfile,
      selectedUserProfile,
      setAccessModalOpen,
      setFilerChange,
      setFilters,
      setFiltersOverwrite,
      setIsFilterChanged,
      setIsProfilesLoading,
      setIsTemporaryProfile,
      setMultipleFilters,
      userProfiles,
    ],
  );

  return <UserProfile.Provider value={contextValue}>{children}</UserProfile.Provider>;
};

UserProfileProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default UserProfileProvider;

export const useProfileContext = () => useContext(UserProfile);
